import { legacyState } from '~/legacy/screens'
import { render, todayISO } from '~/legacy/runtime'
import { apiCreate, apiUpdate } from '~/composables/useApi'
import type { Row } from '~/composables/useDb'

/**
 * Écritures du parcours de Caisse (« Traiter » sur une inscription en attente de paiement, dans Caisse → Inscriptions en attente) :
 * génération de l'échéancier (avec réduction et frais supplémentaires), enregistrement du ou des versements, émission du reçu
 * et de la carte étudiante. Le code de la maquette (`wizardCreateEcheancier`, `wizardCreateVersement`) écrivait directement dans
 * sa base en mémoire ; ici, chaque écriture passe par l'API Java, avec les mêmes règles et les mêmes calculs.
 *
 * Le choix du type de démarche (nouvelle inscription / réinscription) affiché en tête du parcours n'est pas converti : seul
 * le chemin déclenché par « Traiter » (`startCaisseTraitement`, repris tel quel de la maquette) est implémenté ici.
 */

type WizardData = {
  etudiantId: string
  inscriptionId: string
  classeId: string | null
  anneeId: string
  arrears: number
  fraisSupp?: { montant: number, tarificationId: string }[]
  fraisVersement1?: { montant: number, tarificationId: string }[]
  fraisVersement2?: { montant: number, tarificationId: string }[]
  versementExtraIds?: string[]
  echeancierId?: string
  versementInscriptionId?: string
  versementScolariteId?: string | null
  versementId?: string
  recuInscId?: string
  recuScolId?: string | null
  carteId?: string
}
type Wizard = { mode: string | null, step: number, maxStep: number, data: WizardData }
const wizard = (): Wizard => legacyState.wizard as unknown as Wizard

function errorMessage(): string {
  return 'Opération impossible.'
}

function inputValue(id: string): string {
  return (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value ?? ''
}

async function apiRemove(collection: string, id: string): Promise<void> {
  await $fetch(`/api/${collection}/${id}`, { method: 'DELETE' })
  const db = useDb()
  db.value = { ...db.value, [collection]: (db.value[collection] ?? []).filter(r => r.id !== id) }
}

/** `wizardFraisInscriptionForClasse` de la maquette. */
function fraisInscriptionForClasse(classeId: string | null): Row | null {
  const classe = classeId ? findById('classes', classeId) : null
  if (!classe) return null
  return (useDb().value.tarifications ?? []).find(t => isTarifKind(t, 'INSCRIPTION') && t.niveauId === classe.niveauId && t.ecoleId === useCurrentEcoleId().value) ?? null
}

/** `calendrierTranches` de la maquette. */
function calendrierTranches(anneeId: string): Row[] {
  return (useDb().value.tranches ?? []).filter(t => t.anneeId === anneeId).slice().sort((a, b) => Number(a.ordre) - Number(b.ordre))
}

/** `lignesFromNombreVersements` de la maquette : une ligne par tranche du calendrier de l'année, sinon réparties sur le nombre demandé. */
function planLignes(montantNet: number, nombreVersements: number): { libelle: string, montantDu: number, dateEcheance: string }[] {
  const annee = currentAnnee()
  const cal = annee ? calendrierTranches(annee.id) : []
  if (cal.length > 0) {
    let cumul = 0
    return cal.map((t, i) => {
      const montant = i === cal.length - 1 ? Math.round(montantNet - cumul) : Math.round(montantNet / cal.length)
      cumul += montant
      return { libelle: String(t.libelle), montantDu: montant, dateEcheance: String(t.dateEcheance) }
    })
  }
  const n = Math.max(1, Math.round(nombreVersements || 1))
  const today = new Date(todayISO())
  const start = annee ? new Date(Math.max(new Date(String(annee.dateDebut)).getTime(), today.getTime())) : today
  const end = annee ? new Date(String(annee.dateFin)) : new Date(today.getTime() + 300 * 86400000)
  const spanMs = Math.max(end.getTime() - start.getTime(), n * 7 * 86400000)
  let cumul = 0
  const out: { libelle: string, montantDu: number, dateEcheance: string }[] = []
  for (let i = 0; i < n; i++) {
    const isLast = i === n - 1
    const montant = isLast ? Math.round(montantNet - cumul) : Math.round(montantNet / n)
    cumul += montant
    const due = new Date(start.getTime() + spanMs * (i + 1) / n)
    out.push({ libelle: n === 1 ? 'Tranche unique' : `Tranche ${i + 1}/${n}`, montantDu: montant, dateEcheance: due.toISOString().slice(0, 10) })
  }
  return out
}

/** `wizardCreateEcheancier` de la maquette : étape 1 du parcours de Caisse. */
export async function wizardCreateEcheancier(tarifId: string): Promise<void> {
  const w = wizard()
  const tarif = findById('tarifications', tarifId)
  if (!tarif) {
    showToast(errorMessage())
    return
  }
  const arrears = w.data.arrears || 0
  const fraisSupp = w.data.fraisSupp || []
  const fraisSuppTotal = fraisSupp.reduce((s, f) => s + f.montant, 0)
  const montantBrut = Math.round(Number(tarif.montant)) + arrears + fraisSuppTotal
  const existing = w.data.echeancierId ? findById('echeanciers', w.data.echeancierId) : null
  try {
    const echeancier = existing
      ? await apiUpdate('echeanciers', existing.id as string, {
          inscriptionId: w.data.inscriptionId, montantBrut, montantRemise: existing.montantRemise ?? 0,
          montantNet: montantBrut - Number(existing.montantRemise ?? 0), statut: existing.statut,
        })
      : await apiCreate('echeanciers', { inscriptionId: w.data.inscriptionId, montantBrut, montantRemise: 0, montantNet: montantBrut, statut: 'A_JOUR' })

    // Frais supplémentaires déjà enregistrés lors d'un passage précédent : on ne recrée que les nouveaux (compté par tarification,
    // pour ne pas bloquer l'ajout d'un deuxième frais identique quand un seul avait déjà été enregistré).
    const existingFraisSupp = existing ? (useDb().value.fraisSupplementaires ?? []).filter(f => f.inscriptionId === w.data.inscriptionId) : []
    const remainingExisting = new Map<string, number>()
    for (const ef of existingFraisSupp) remainingExisting.set(ef.tarificationId as string, (remainingExisting.get(ef.tarificationId as string) ?? 0) + 1)
    for (const f of fraisSupp) {
      const remaining = remainingExisting.get(f.tarificationId) ?? 0
      if (remaining > 0) {
        remainingExisting.set(f.tarificationId, remaining - 1)
        continue
      }
      await apiCreate('fraisSupplementaires', { inscriptionId: w.data.inscriptionId, tarificationId: f.tarificationId, montant: f.montant, dateApplication: todayISO() })
    }

    const reductionId = inputValue('w-reduction')
    if (reductionId) {
      const reduction = findById('reductions', reductionId)
      if (reduction) {
        const montantCalcule = reduction.type === 'POURCENTAGE' ? Math.round(montantBrut * Number(reduction.valeur) / 100) : Number(reduction.valeur)
        await apiCreate('reductionsAppliquees', { inscriptionId: w.data.inscriptionId, reductionId: reduction.id, montantCalcule, dateApplication: todayISO(), justificatif: 'Appliquée via le parcours de démonstration' })
      }
    }
    await recomputeEcheancierRemise(w.data.inscriptionId)
    const montantNet = Number((findById('echeanciers', echeancier.id)?.montantNet) ?? montantBrut)
    const nombreVersements = Number(tarif.nombreTranches)

    // Les tranches déjà (partiellement) payées ne sont jamais touchées : on ne régénère que la part encore due.
    const existingLignes = existing
      ? (useDb().value.lignesEcheance ?? []).filter(l => l.echeancierId === echeancier.id).slice().sort((a, b) => Number(a.numeroTranche) - Number(b.numeroTranche))
      : []
    const paidLignes = existingLignes.filter(l => Number(l.montantPaye || 0) > 0)
    const unpaidLignes = existingLignes.filter(l => !(Number(l.montantPaye || 0) > 0))

    if (existingLignes.length === 0) {
      const lignes = planLignes(montantNet, nombreVersements)
      for (const [i, l] of lignes.entries()) {
        await apiCreate('lignesEcheance', { echeancierId: echeancier.id, numeroTranche: i + 1, libelle: l.libelle, montantDu: l.montantDu, montantPaye: 0, dateEcheance: l.dateEcheance, statut: 'ATTENDU' })
      }
    }
    else {
      const totalPaidPortion = paidLignes.reduce((s, l) => s + Number(l.montantDu), 0)
      const remaining = Math.max(montantNet - totalPaidPortion, 0)
      for (const l of unpaidLignes) await apiRemove('lignesEcheance', l.id as string)
      const nbRestant = Math.max(nombreVersements - paidLignes.length, 1)
      const nouvellesLignes = planLignes(remaining, nbRestant)
      for (const [i, l] of nouvellesLignes.entries()) {
        await apiCreate('lignesEcheance', {
          echeancierId: echeancier.id, numeroTranche: paidLignes.length + i + 1, libelle: l.libelle,
          montantDu: l.montantDu, montantPaye: 0, dateEcheance: l.dateEcheance, statut: 'ATTENDU',
        })
      }
    }
    w.data.echeancierId = echeancier.id as string
  }
  catch (e) {
    console.error('[Registre] useWizardCaisse:', e)
    showToast(errorMessage())
    return
  }
  w.step += 1
  w.maxStep = Math.max(w.maxStep || 0, w.step)
  render()
}

/** `wizardCreateVersement` de la maquette : étape 2 (versements) puis émission du reçu et de la carte (étape 3). */
export async function wizardCreateVersement(): Promise<void> {
  const w = wizard()
  const payChoice = inputValue('w-pay-choice')
  const mode = inputValue('w-mode')
  const date = inputValue('w-datepaiement') || todayISO()
  const tarificationIdInsc = inputValue('w-type-insc')
  const tarifInsc = fraisInscriptionForClasse(w.data.classeId)
  const montantInsc = tarifInsc ? Math.round(Number(tarifInsc.montant)) : 0

  try {
    const existingVersInsc = w.data.versementInscriptionId ? findById('versements', w.data.versementInscriptionId) : null
    const versementInsc = existingVersInsc
      ? await apiUpdate('versements', existingVersInsc.id as string, { inscriptionId: w.data.inscriptionId, tarificationId: tarificationIdInsc, montant: montantInsc, datePaiement: date, modePaiement: mode, statut: 'ENREGISTRE' })
      : await apiCreate('versements', { inscriptionId: w.data.inscriptionId, tarificationId: tarificationIdInsc, montant: montantInsc, datePaiement: date, modePaiement: mode, statut: 'ENREGISTRE' })
    w.data.versementInscriptionId = versementInsc.id as string

    const extraIds: string[] = []
    for (const f of (w.data.fraisVersement1 ?? [])) {
      const extra = await apiCreate('versements', { inscriptionId: w.data.inscriptionId, tarificationId: f.tarificationId, montant: Math.round(f.montant), datePaiement: date, modePaiement: mode, statut: 'ENREGISTRE' })
      extraIds.push(extra.id as string)
    }

    let versementScol: Row | null = null
    if (payChoice === 'INSC_PLUS_SCOL') {
      const tarificationIdScol = inputValue('w-type-scol')
      const montantScol = Number(inputValue('w-montant-scol')) || 0
      if (montantScol > 0) {
        const existingVersScol = w.data.versementScolariteId ? findById('versements', w.data.versementScolariteId) : null
        const montantScolAvant = existingVersScol ? Number(existingVersScol.montant) : 0
        versementScol = existingVersScol
          ? await apiUpdate('versements', existingVersScol.id as string, { inscriptionId: w.data.inscriptionId, tarificationId: tarificationIdScol, montant: montantScol, datePaiement: date, modePaiement: mode, statut: 'ENREGISTRE' })
          : await apiCreate('versements', { inscriptionId: w.data.inscriptionId, tarificationId: tarificationIdScol, montant: montantScol, datePaiement: date, modePaiement: mode, statut: 'ENREGISTRE' })

        const tarifScol = findById('tarifications', tarificationIdScol)
        if (tarifScol && isTarifKind(tarifScol, 'SCOLARITE')) {
          const lignes = (useDb().value.lignesEcheance ?? []).filter(l => l.echeancierId === w.data.echeancierId).slice().sort((a, b) => Number(a.numeroTranche) - Number(b.numeroTranche))
          const first = lignes[0]
          if (first) {
            const existingImputation = existingVersScol ? (useDb().value.imputations ?? []).find(i => i.versementId === (versementScol as Row).id) : null
            if (existingImputation) {
              await apiUpdate('imputations', existingImputation.id as string, { versementId: versementScol.id, ligneEcheanceId: first.id, montantImpute: montantScol, dateImputation: new Date().toISOString() })
            }
            else {
              await apiCreate('imputations', { versementId: versementScol.id, ligneEcheanceId: first.id, montantImpute: montantScol, dateImputation: new Date().toISOString() })
            }
            const montantPayeAvant = Number(first.montantPaye || 0)
            const nouveauPaye = Math.max(montantPayeAvant - montantScolAvant + montantScol, 0)
            const paye = Math.min(nouveauPaye, Number(first.montantDu))
            const statut = paye >= Number(first.montantDu) ? 'PAYE' : (paye > 0 ? 'PARTIEL' : 'ATTENDU')
            await apiUpdate('lignesEcheance', first.id as string, { echeancierId: first.echeancierId, numeroTranche: first.numeroTranche, libelle: first.libelle, montantDu: first.montantDu, montantPaye: paye, dateEcheance: first.dateEcheance, statut })
          }
        }
      }
    }
    for (const f of (w.data.fraisVersement2 ?? [])) {
      const extra = await apiCreate('versements', { inscriptionId: w.data.inscriptionId, tarificationId: f.tarificationId, montant: Math.round(f.montant), datePaiement: date, modePaiement: mode, statut: 'ENREGISTRE' })
      extraIds.push(extra.id as string)
    }
    w.data.versementExtraIds = extraIds

    w.data.versementScolariteId = versementScol ? (versementScol.id as string) : (w.data.versementScolariteId ?? null)
    w.data.versementId = versementScol ? (versementScol.id as string) : (w.data.versementScolariteId ?? (versementInsc.id as string))

    if (w.mode === 'CAISSE') {
      const inscription = findById('inscriptions', w.data.inscriptionId)
      if (inscription && inscription.statut === 'EN_ATTENTE_PAIEMENT') {
        await apiUpdate('inscriptions', inscription.id as string, {
          numero: inscription.numero, etudiantId: inscription.etudiantId, classeId: inscription.classeId, anneeId: inscription.anneeId,
          typeInscription: inscription.typeInscription, statut: 'VALIDEE', dateDemande: inscription.dateDemande, moyenneGenerale: inscription.moyenneGenerale, ecoleId: inscription.ecoleId,
        })
        await apiCreate('historiqueStatutInscription', {
          inscriptionId: inscription.id, statutPrecedent: 'EN_ATTENTE_PAIEMENT', statutNouveau: 'VALIDEE',
          dateChangement: new Date().toISOString(), motif: 'Traitement financier complété en Caisse',
        })
      }
    }

    const existingRecuInsc = w.data.recuInscId ? findById('recus', w.data.recuInscId) : null
    const recuInsc = existingRecuInsc
      ? await apiUpdate('recus', existingRecuInsc.id as string, { versementId: versementInsc.id, montant: versementInsc.montant, dateEmission: existingRecuInsc.dateEmission, annule: false })
      : await apiCreate('recus', { versementId: versementInsc.id, montant: versementInsc.montant, dateEmission: todayISO(), annule: false })
    w.data.recuInscId = recuInsc.id as string
    if (versementScol) {
      const existingRecuScol = w.data.recuScolId ? findById('recus', w.data.recuScolId) : null
      const recuScol = existingRecuScol
        ? await apiUpdate('recus', existingRecuScol.id as string, { versementId: versementScol.id, montant: versementScol.montant, dateEmission: existingRecuScol.dateEmission, annule: false })
        : await apiCreate('recus', { versementId: versementScol.id, montant: versementScol.montant, dateEmission: todayISO(), annule: false })
      w.data.recuScolId = recuScol.id as string
    }

    const existingCarte = (useDb().value.cartesEtudiantes ?? []).find(c => c.inscriptionId === w.data.inscriptionId)
    if (existingCarte) {
      w.data.carteId = existingCarte.id as string
    }
    else {
      const exp = new Date(todayISO())
      exp.setFullYear(exp.getFullYear() + 1)
      const carte = await apiCreate('cartesEtudiantes', {
        inscriptionId: w.data.inscriptionId, dateEmission: todayISO(), dateExpiration: exp.toISOString().slice(0, 10), statut: 'ACTIVE',
      })
      w.data.carteId = carte.id as string
    }
  }
  catch (e) {
    console.error('[Registre] useWizardCaisse:', e)
    showToast(errorMessage())
    return
  }
  w.step += 1
  w.maxStep = Math.max(w.maxStep || 0, w.step)
  render()
}
